import { useState } from "react";
import * as routes from "../../routes";
import MenuItem from "./MenuItem";

import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  useMediaQuery,
  Drawer as MuiDrawer,
  List,
  Divider,
  IconButton,
  ClickAwayListener,
  Typography,
} from "@mui/material";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export default function Menu(props: any) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const menuItems = routes.mainMenu();
  const internalMenuItems = routes.internalMenu();

  const emptyMenu = menuItems.length === 0 && internalMenuItems.length === 0;
  const [open, setOpen] = useState(!isMobile && !emptyMenu);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleClickAway = () => {
    if (isMobile) {
      handleDrawerClose();
    }
  };

  return (
    <>
      {isMobile && !open ? (
        <IconButton
          onClick={handleDrawerOpen}
          sx={{
            position: "fixed",
            float: "left",
            backgroundColor: "secondary.main",
            zIndex: 12000,
            borderRadius: 0,
            minHeight: theme.mixins.toolbar.minHeight,
            minWidth: theme.mixins.toolbar.minHeight,
            color: theme.palette.primary.contrastText,
          }}
        >
          <MenuIcon />
        </IconButton>
      ) : (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Drawer
            variant="permanent"
            onClose={handleDrawerClose}
            open={open}
            anchor="left"
            PaperProps={{
              sx: {
                backgroundColor: "primary.main",
                color: "white",
              },
            }}
          >
            <DrawerHeader
              sx={{ backgroundColor: "primary.main", color: "white" }}
            >
              {open ? (
                <>
                  {/* <img
                    style={{ maxWidth: "70%" }}
                    src={process.env.PUBLIC_URL + "/mcf_logo_2.png"}
                    alt="MyCareFlex"
                  /> */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    MyCareFlex
                  </Typography>
                  <IconButton
                    onClick={handleDrawerClose}
                    sx={{ color: theme.palette.primary.contrastText }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  onClick={handleDrawerOpen}
                  sx={{ color: theme.palette.primary.contrastText }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </DrawerHeader>

            {menuItems.length > 0 && (
              <>
                <Divider sx={{ borderWidth: "2px" }} />
                <List sx={{ padding: 0 }}>
                  {menuItems.map((element: any, index: number) => (
                    <MenuItem
                      key={index}
                      element={element}
                      open={open}
                      onClick={handleClickAway}
                    />
                  ))}
                  {internalMenuItems.length !== 0 && (
                    <>
                      <Divider
                        component={"div"}
                        textAlign="left"
                        sx={{
                          borderColor: theme.palette.primary.contrastText,
                          ...(!open ? { opacity: 0.5 } : {}),
                          "&::before, &::after": {
                            borderColor: theme.palette.primary.contrastText,
                            opacity: 0.5,
                          },
                        }}
                      >
                        {open && "Internal"}
                      </Divider>
                      {internalMenuItems
                        .map((element: any, index: number) => (
                          <MenuItem
                            key={index}
                            element={element}
                            open={open}
                            onClick={handleClickAway}
                          />
                        ))}
                    </>
                  )}
                </List>
              </>
            )}
          </Drawer>
        </ClickAwayListener>
      )}
    </>
  );
}
